import React, { useState } from 'react';
import { Wallet, Plus, Trash2, Edit2, DollarSign, TrendingUp, Upload, Download } from 'lucide-react';
import { useCountry } from './CountryContext';
import { getCurrencySymbol, getAccountTypes, getCurrencyCode } from './countryConfig';
import { getAccountsWithBalances } from './utils/accountUtils';
import { getInvestmentMarketValue } from './utils/netWorthCalculations';
import { usePortfolioPrices } from './hooks/usePricing';
import CSVImportModal from './components/CSVImportModal';
import {
  generateAccountsTemplate,
  downloadCSV,
  validateAccountsCSV,
  convertCSVToAccounts,
  exportAccountsToCSV
} from './utils/csvUtils';
import './AccountTypeManager.css';

const AccountTypeManager = ({ userAccounts, investments = [], exchangeRates = null, onAddAccount, onRemoveAccount, onUpdateAccount }) => {
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);
  const configAccountTypes = getAccountTypes(selectedCountry);

  console.log('AccountTypeManager - Selected Country:', selectedCountry);
  console.log('AccountTypeManager - Config Account Types:', configAccountTypes);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newAccount, setNewAccount] = useState({
    name: '',
    type: '',
    institution: '',
    contributionAmount: '',
    contributionFrequency: 'monthly'
  });

  // Edit account state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  // CSV import state
  const [showImportModal, setShowImportModal] = useState(false);

  // Map account types from config to format expected by component
  const accountTypes = configAccountTypes ? configAccountTypes.map(acc => ({
    value: acc.id,
    label: acc.name,
    icon: acc.type === 'retirement' || acc.type === 'pension' ? '🏦' :
          acc.type === 'isa' ? '💰' :
          acc.type === 'health' ? '🏥' :
          acc.type === 'education' ? '🎓' :
          acc.type === 'crypto' ? '₿' :
          acc.type === 'taxable' ? '📈' : '💵',
    description: acc.description
  })) : [];

  const handleAdd = async () => {
    if (!newAccount.name || !newAccount.type) {
      alert('Please fill in all required fields');
      return;
    }

    // ⭐ Determine accountPurpose from account type config
    const selectedAccountType = configAccountTypes.find(acc => acc.id === newAccount.type);
    const accountPurpose = selectedAccountType?.type === 'retirement' || selectedAccountType?.type === 'pension'
      ? 'retirement'
      : selectedAccountType?.type === 'taxable'
      ? 'taxable'
      : selectedAccountType?.type === 'crypto'
      ? 'crypto'
      : 'savings';

    const account = {
      name: newAccount.name,
      type: newAccount.type,
      // ⭐ REMOVED: No more manual balance entry - calculated from investments
      institution: newAccount.institution || 'Not specified',
      accountPurpose,
      contributionAmount: newAccount.contributionAmount ? parseFloat(newAccount.contributionAmount) : 0,
      contributionFrequency: newAccount.contributionFrequency || 'monthly',
      addedAt: new Date().toISOString()
    };

    try {
      await onAddAccount(account);
      setNewAccount({ name: '', type: '', institution: '', contributionAmount: '', contributionFrequency: 'monthly' });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding account:', error);
      alert('Failed to add account. Please try again.');
    }
  };

  // Edit account handlers
  const handleEditClick = (account) => {
    setEditingAccount({
      ...account,
      institution: account.institution || '',
      contributionAmount: account.contributionAmount || '',
      contributionFrequency: account.contributionFrequency || 'monthly'
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingAccount.name || !editingAccount.type) {
      alert('Please fill in all required fields');
      return;
    }

    // ⭐ Determine accountPurpose from account type config
    const selectedAccountType = configAccountTypes.find(acc => acc.id === editingAccount.type);
    const accountPurpose = selectedAccountType?.type === 'retirement' || selectedAccountType?.type === 'pension'
      ? 'retirement'
      : selectedAccountType?.type === 'taxable'
      ? 'taxable'
      : selectedAccountType?.type === 'crypto'
      ? 'crypto'
      : 'savings';

    const updatedAccount = {
      ...editingAccount,
      institution: editingAccount.institution || 'Not specified',
      accountPurpose,
      contributionAmount: editingAccount.contributionAmount ? parseFloat(editingAccount.contributionAmount) : 0,
      contributionFrequency: editingAccount.contributionFrequency || 'monthly'
    };

    try {
      // Extract id from updatedAccount and pass id and updates separately
      const { id, ...updates } = updatedAccount;
      await onUpdateAccount(id, updates);
      setEditingAccount(null);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating account:', error);
      alert('Failed to update account. Please try again.');
    }
  };

  const getAccountTypeInfo = (typeValue) => {
    return accountTypes.find(t => t.value === typeValue) || accountTypes[0];
  };

  // CSV Import/Export Handlers
  const handleDownloadTemplate = () => {
    const template = generateAccountsTemplate();
    downloadCSV(template, 'accounts-import-template.csv');
  };

  const handleImportCSV = async (data) => {
    // Convert CSV data to account objects
    const accounts = convertCSVToAccounts(data, configAccountTypes);

    // Import accounts one by one
    const results = { success: 0, failed: 0, errors: [] };

    for (const account of accounts) {
      try {
        await onAddAccount(account);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push({ account: account.name, error: error.message });
      }
    }

    return results;
  };

  const handleExportCSV = () => {
    const csv = exportAccountsToCSV(userAccounts);
    downloadCSV(csv, 'planlens-accounts-export.csv');
  };

  // ⭐ LIVE PRICING: Fetch live prices for investments
  const { prices: priceMap, loading: pricesLoading } = usePortfolioPrices(investments);

  // ⭐ Calculate total balance including unassigned investments with LIVE prices
  const displayCurrency = getCurrencyCode(selectedCountry);
  const accountsWithBalances = getAccountsWithBalances(userAccounts, investments, displayCurrency, exchangeRates, priceMap);

  // Get total from all accounts
  const assignedTotal = accountsWithBalances.reduce((sum, acc) => sum + acc.calculatedBalance, 0);

  // ⭐ Add unassigned investments (those without an accountId)
  // Use getInvestmentMarketValue with live prices for consistency
  const unassignedInvestments = investments.filter(inv => !inv.accountId || inv.accountId === '');
  const unassignedTotal = unassignedInvestments.reduce((sum, inv) => {
    const marketValue = getInvestmentMarketValue(inv, priceMap);
    const investmentCurrency = inv.currency || displayCurrency;

    // Convert to display currency if needed
    if (exchangeRates && exchangeRates.rates && investmentCurrency !== displayCurrency) {
      const fromRate = exchangeRates.rates[investmentCurrency];
      const toRate = exchangeRates.rates[displayCurrency];
      if (fromRate && toRate) {
        return sum + ((marketValue / fromRate) * toRate);
      }
    }
    return sum + marketValue;
  }, 0);

  const totalBalance = assignedTotal + unassignedTotal;

  // ⭐ MULTI-CURRENCY FIX: Group accounts by type, including those from other countries
  const validAccountTypeIds = new Set(accountTypes.map(t => t.value));
  const matchingAccounts = accountsWithBalances.filter(acc => validAccountTypeIds.has(acc.type));
  const otherCountryAccounts = accountsWithBalances.filter(acc => !validAccountTypeIds.has(acc.type));

  const accountsByType = accountTypes.map(type => {
    const accounts = matchingAccounts.filter(acc => acc.type === type.value);
    const total = accounts.reduce((sum, acc) => sum + acc.calculatedBalance, 0);
    return {
      ...type,
      accounts,
      total,
      count: accounts.length
    };
  }).filter(type => type.count > 0);

  // ⭐ Add "Other Country Accounts" group if there are accounts from other countries
  if (otherCountryAccounts.length > 0) {
    const otherTotal = otherCountryAccounts.reduce((sum, acc) => sum + acc.calculatedBalance, 0);
    accountsByType.push({
      value: 'other_country',
      label: 'Other Country Accounts',
      icon: '🌍',
      description: 'Accounts from other country settings',
      accounts: otherCountryAccounts,
      total: otherTotal,
      count: otherCountryAccounts.length
    });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white shadow-lg">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold mb-2">Account Management</h2>
            <p className="text-blue-100">Track all your financial accounts</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Download Template */}
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all whitespace-nowrap"
              title="Download CSV template"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">Template</span>
            </button>

            {/* Import CSV */}
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all whitespace-nowrap"
              title="Import accounts from CSV"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">Import CSV</span>
            </button>

            {/* Export CSV */}
            {userAccounts.length > 0 && (
              <button
                onClick={handleExportCSV}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all whitespace-nowrap"
                title="Export accounts to CSV"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">Export CSV</span>
              </button>
            )}

            {/* Add Account */}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all shadow-md hover:shadow-lg font-semibold whitespace-nowrap"
            >
              <Plus className="w-5 h-5" />
              <span className="text-sm sm:text-base">Add Account</span>
            </button>
          </div>
        </div>
      </div>

      {/* Total Balance Card */}
      {userAccounts.length > 0 && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 mb-2">Total Balance Across All Accounts</p>
              <p className="text-5xl font-bold">{currencySymbol}{totalBalance.toLocaleString()}</p>
              <p className="text-blue-100 mt-2">{userAccounts.length} accounts</p>
            </div>
            <Wallet className="w-20 h-20 text-blue-200 opacity-50" />
          </div>
        </div>
      )}

      {/* Accounts by Type */}
      {accountsByType.length > 0 ? (
        <div className="space-y-6">
          {accountsByType.map(typeGroup => (
            <div key={typeGroup.value} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{typeGroup.icon}</span>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{typeGroup.label}</h3>
                    <p className="text-sm text-gray-600">{typeGroup.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{currencySymbol}{typeGroup.total.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">{typeGroup.count} account{typeGroup.count !== 1 ? 's' : ''}</p>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                {typeGroup.accounts.map(account => (
                  <div key={account.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <DollarSign className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-semibold text-gray-900">{account.name}</p>
                          <p className="text-sm text-gray-600">{account.institution}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">{currencySymbol}{account.calculatedBalance.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{account.holdingsCount} holdings</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditClick(account)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onRemoveAccount(account.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
          <Wallet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">No accounts yet</p>
          <p className="text-sm text-gray-500">Add your first account to start tracking your finances</p>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Add Financial Account</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Type *
                </label>
                <select
                  value={newAccount.type}
                  onChange={(e) => setNewAccount({ ...newAccount, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select account type</option>
                  {accountTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Name *
                </label>
                <input
                  type="text"
                  value={newAccount.name}
                  onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., My Retirement Fund"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Financial Institution (Optional)
                </label>
                <input
                  type="text"
                  value={newAccount.institution}
                  onChange={(e) => setNewAccount({ ...newAccount, institution: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Vanguard, Fidelity, Chase"
                />
              </div>

              {/* Contribution Tracking */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regular Contribution Amount (Optional)
                </label>
                <div className="flex space-x-3">
                  <input
                    type="number"
                    value={newAccount.contributionAmount}
                    onChange={(e) => setNewAccount({ ...newAccount, contributionAmount: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 500"
                  />
                  <select
                    value={newAccount.contributionFrequency}
                    onChange={(e) => setNewAccount({ ...newAccount, contributionFrequency: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Track regular contributions to this account for better planning
                </p>
              </div>

              {/* Info about calculated balances */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">ℹ</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Balance Calculated Automatically</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Account balances are calculated from the investments held in them. After creating this account, link your investments to it to see the balance.
                    </p>
                  </div>
                </div>
              </div>

              {newAccount.type && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{getAccountTypeInfo(newAccount.type).icon}</span>
                    <div>
                      <p className="font-semibold text-blue-900">{getAccountTypeInfo(newAccount.type).label}</p>
                      <p className="text-sm text-blue-700">{getAccountTypeInfo(newAccount.type).description}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleAdd}
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold"
              >
                Add Account
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewAccount({ name: '', type: '', institution: '', contributionAmount: '', contributionFrequency: 'monthly' });
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditModal && editingAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Account</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Type *
                </label>
                <select
                  value={editingAccount.type}
                  onChange={(e) => setEditingAccount({ ...editingAccount, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select account type</option>
                  {accountTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Name *
                </label>
                <input
                  type="text"
                  value={editingAccount.name}
                  onChange={(e) => setEditingAccount({ ...editingAccount, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., My Retirement Fund"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Financial Institution (Optional)
                </label>
                <input
                  type="text"
                  value={editingAccount.institution}
                  onChange={(e) => setEditingAccount({ ...editingAccount, institution: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Vanguard, Fidelity, Chase"
                />
              </div>

              {/* Contribution Tracking */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regular Contribution Amount (Optional)
                </label>
                <div className="flex space-x-3">
                  <input
                    type="number"
                    value={editingAccount.contributionAmount}
                    onChange={(e) => setEditingAccount({ ...editingAccount, contributionAmount: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 500"
                  />
                  <select
                    value={editingAccount.contributionFrequency}
                    onChange={(e) => setEditingAccount({ ...editingAccount, contributionFrequency: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Track regular contributions to this account for better planning
                </p>
              </div>

              {/* Info about calculated balances */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs font-bold">ℹ</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Balance Calculated Automatically</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Account balances are calculated from the investments held in this account. Changing the account type will update its purpose classification.
                    </p>
                  </div>
                </div>
              </div>

              {editingAccount.type && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{getAccountTypeInfo(editingAccount.type).icon}</span>
                    <div>
                      <p className="font-semibold text-blue-900">{getAccountTypeInfo(editingAccount.type).label}</p>
                      <p className="text-sm text-blue-700">{getAccountTypeInfo(editingAccount.type).description}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleUpdate}
                className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAccount(null);
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={showImportModal}
        title="Import Accounts from CSV"
        type="accounts"
        onClose={() => setShowImportModal(false)}
        onDownloadTemplate={handleDownloadTemplate}
        onValidate={(data) => validateAccountsCSV(data, configAccountTypes)}
        onImport={handleImportCSV}
        templateFilename="accounts-import-template.csv"
        instructions={
          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>CSV Format Requirements:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Headers: Account Name, Account Type, Institution, Contribution Amount, Contribution Frequency</li>
              <li>Account Type must match one of your country's account types (e.g., '401k', 'roth_ira', 'stocks_shares_isa')</li>
              <li>Contribution Amount should be a number (e.g., 500)</li>
              <li>Contribution Frequency should be: monthly, quarterly, or annual</li>
              <li>Download the template to see examples</li>
            </ul>
          </div>
        }
      />
    </div>
  );
};

export default AccountTypeManager;
